const ACTION_NAMESPACE = 'Experience Entity => experiences => ';

import { createActionFactory } from '../../common/redux';

const { createAction, createApiFailureAction } = createActionFactory(ACTION_NAMESPACE);

const changeDisplayType = createAction('change display type', (displayType) => ({ displayType }));
const updateNavConfig = createAction('update nav config', (key, config) => ({ key, config }));

const loadExperience = createAction('load experience', (id, forceReload) => ({ id, forceReload }));
const experienceReloadNotNeeded = createAction('experience reload not needed');

const updateTheme = createAction('update theme', (experience) => ({ experience }));
const updateThemeSuccess = createAction('succesfully updated theme');
const updateThemeFailure = createApiFailureAction('failed to update theme');

const updateFooterConfig = createAction('update footer config', (footerConfig) => ({ footerConfig }));

const approve = createAction('approve experience', (id) => ({ id }));
const unapprove = createAction('unapprove experience', (id) => ({ id }));

const publish = createAction('publish experience', (id, expiryDate) => ({ id, expiryDate }));
const republish = createAction('republish experience', (id, publishDate, expiryDate) => ({
  id,
  publishDate,
  expiryDate,
}));

const expire = createAction('expire experience', (id) => ({ id }));

const unschedule = createAction('reschedule experience', (id) => ({
  id,
}));
const reschedule = createAction('reschedule experience', (id, publishDate, expiryDate) => ({
  id,
  publishDate,
  expiryDate,
}));
const scheduleExpiration = createAction('schedule expiration', (id, expiryDate) => ({ id, expiryDate }));

const showResetExperienceDataModal = createAction('opening reset experience data warning modal');
const hideResetExperienceDataModal = createAction('closing reset experience data warning modal');

const resetExperienceData = createAction('reset experience data');
const resetExperienceDataSuccess = createAction('reset experience data success', (experience) => ({ experience }));
const resetExperienceDataFailure = createApiFailureAction('reset experience data failure');

const requestResetExperienceData = createAction('request reset experience data', (experienceId) => ({ experienceId }));
const requestResetExperienceDataFailure = createApiFailureAction('failed to reset experience data');

const requestDeleteExperience = createAction('request delete experience', (experienceId) => ({ experienceId }));
const requestDeleteExperienceSuccess = createAction('successfully requested delete experience');
const requestDeleteExperienceFailure = createApiFailureAction('failed to request delete experience', (err) => ({
  err,
}));

const confirmDeleteExperience = createAction('confirm delete experience', (experienceId) => ({ experienceId }));
const confirmDeleteExperienceSuccess = createAction('successfully confirm delete experience');
const confirmDeleteExperienceFailure = createAction('failed to confirm delete experience', (err) => ({ err }));

const requestExpireExperience = createAction('request expire experience', (id) => ({ id }));

const deleteExperience = createAction('delete experience', (experienceId) => ({ experienceId }));
const deleteExperienceSuccess = createAction('successfully deleted experience', (experienceId) => ({ experienceId }));
const deleteExperienceFailure = createApiFailureAction('failed to delete experience');

const changeExperience = createAction('change experience', (key, value) => ({ key, value }));

const fetchExperience = createAction('fetch experience', (id) => ({ id }));
const fetchExperienceFailure = createApiFailureAction('fetch experience object failure');
const fetchExperienceSuccess = createAction('fetch experience object success', (experience) => ({
  experience,
}));

const setActiveExperience = createAction('set active experience', (experience) => ({
  experience,
}));

const fetchExperiences = createAction(
  'fetch experiences',
  (status, sort, order, searchFilter, offset, max, includeSubaccountsFilter) => ({
    status,
    sort,
    order,
    searchFilter,
    offset,
    max,
    includeSubaccountsFilter,
  })
);
const fetchExperiencesFailure = createApiFailureAction('fetch experiences failure');
const fetchExperiencesSuccess = createAction('fetch experiences success', (experiences, count, lastQuery) => ({
  experiences,
  count,
  lastQuery,
}));

const clearExperiences = createAction('clear experiences');

const stageExperience = createAction('stage experience', (campaignId) => ({ campaignId }));

const updateExperience = createAction('update experience');
const updateExperienceFailure = createApiFailureAction('failed to update experience');
const updateExperienceSuccess = createAction('succesfully updated experience', (experience) => ({
  experience,
}));
const updateExperienceStatusSuccess = createAction('successfully updated experience status', (experience) => ({
  experience,
}));
const updateExperienceStatusFailure = createApiFailureAction('failed to updated experience status');

const updateMacroExperienceSettings = createAction('update macro experience settings');
const updateMacroExperienceSettingsSuccess = createAction('update macro experience settings success');
const updateMacroExperienceSettingsFailure = createAction('update macro experience settings failure');

const updateExperienceBackground = createAction('update experience background', (backgroundAssetRef) => ({
  backgroundAssetRef,
}));
const updateExperienceBackgroundSuccess = createAction(
  'update experience background success',
  ({ backgroundAssetRef, lastUpdated, modifiedBy }) => ({ backgroundAssetRef, lastUpdated, modifiedBy })
);
const updateExperienceBackgroundFailure = createApiFailureAction('failed to update experience background');

const updateExperienceHeader = createAction('update experience header', (headerConfig) => ({
  headerConfig,
}));
const updateExperienceHeaderSuccess = createAction(
  'update experience header success',
  ({ headerConfig, lastUpdated, modifiedBy }) => ({ headerConfig, lastUpdated, modifiedBy })
);
const updateExperienceHeaderFailure = createApiFailureAction('failed to update experience header');

// *** *** *** *** *** *** *** *** *** *** *** *** //
//        Privacy Settings Capability              //
// *** *** *** *** *** *** *** *** *** *** *** *** //
const updatePolicyConfig = createAction('update experience policy config', (policyConfig) => ({
  policyConfig,
}));
const updatePolicyConfigSuccess = createAction(
  'update experience policy config success',
  ({ policyConfig, policyConfigJSON }) => ({ policyConfig, policyConfigJSON })
);

const updatePolicyConfigFailure = createApiFailureAction('failed to update experience policy config text', (error) => ({
  error,
}));

const transitionStateSuccess = createAction('transition state success');
const transitionStateFailure = createApiFailureAction('transition state failure');

const hideConfirmDeleteExperienceModal = createAction('hide confirm delete experience modal');
const showConfirmDeleteExperienceModal = createAction('show confirm delete experience modal');

const hideConfirmExpireExperienceModal = createAction('hide confirm expire experience modal');
const showConfirmExpireExperienceModal = createAction('show confirm expire experience modal');

const cloneExperience = createAction('clone experience');
const cloneExperienceSuccess = createAction('clone experience success', (filter) => ({ filter })); // we have the ability to pass in filter options now so we can filter or sort the list after a successful action
const cloneExperienceFailure = createApiFailureAction('clone experience failure');

const fetchExperienceMetrics = createAction('fetch experience metrics', (includeSubaccounts) => ({
  includeSubaccounts,
}));
const fetchExperienceMetricsSuccess = createAction('fetch experience metrics success', (summary) => ({ summary }));
const fetchExperienceMetricsFailure = createApiFailureAction('fetch experience metrics failure', (error) => ({
  error,
}));

const changeMetadata = createAction('change metadata', (value) => value);
const showConfirmUpdateMetadataModal = createAction('show confirm update metadata modal');
const hideConfirmUpdateMetadataModal = createAction('hide confirm update metadata modal');

const updateExternalRef = createAction('continue to update the metadata and update the external ref');
const leaveExternalRef = createAction('continue to update the metadata but not external ref');

const validateExternalRef = createAction('validate external ref');
const validateExternalRefSuccess = createAction('validate external ref success');
const validateExternalRefFailure = createApiFailureAction('validate external ref failure');

const resetDirtyFields = createAction('reset dirty fields');

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Sharing config.
const toggleShare = createAction('toggle sharing for passed network', (network) => ({ network }));
const enableShare = createAction('enable sharing for passed network', (network) => ({ network }));
const disableShare = createAction('disable sharing for passed network', (network) => ({ network }));
const checkShareEnabled = createAction('check network share enabled state', (id, value, network) => ({
  id,
  value,
  network,
}));
const updateSharingData = createAction('update sharing data', (sharingData, network) => ({ sharingData, network }));
const updateSharingDataAttribute = createAction('update single sharing data attribute', (id, value, network) => ({
  id,
  value,
  network,
}));

const updatePresentationConfig = createAction('update attribute in presentationConfig', (field, value) => ({
  field,
  value,
}));

const setQuotaStatus = createAction('add quota status to the experience', (quotaStatusMessages) => ({
  quotaStatusMessages,
}));

export default {
  ACTION_NAMESPACE,

  approve,
  changeDisplayType,
  updateNavConfig,

  loadExperience,
  experienceReloadNotNeeded,

  changeExperience,
  updateTheme,
  updateThemeSuccess,
  updateThemeFailure,

  updateFooterConfig,

  expire,

  fetchExperience,
  fetchExperienceFailure,
  fetchExperienceSuccess,

  fetchExperiences,
  fetchExperiencesFailure,
  fetchExperiencesSuccess,

  setActiveExperience,

  clearExperiences,

  showResetExperienceDataModal,
  hideResetExperienceDataModal,

  resetExperienceData,
  resetExperienceDataSuccess,
  resetExperienceDataFailure,

  requestResetExperienceData,
  requestResetExperienceDataFailure,

  requestDeleteExperience,
  requestDeleteExperienceSuccess,
  requestDeleteExperienceFailure,

  confirmDeleteExperience,
  confirmDeleteExperienceSuccess,
  confirmDeleteExperienceFailure,

  requestExpireExperience,

  deleteExperience,
  deleteExperienceFailure,
  deleteExperienceSuccess,
  publish,
  republish,
  reschedule,
  unschedule,
  scheduleExpiration,
  stageExperience,
  unapprove,
  updateExperience,
  updateExperienceFailure,
  updateExperienceSuccess,
  updateExperienceStatusSuccess,
  updateExperienceStatusFailure,
  transitionStateFailure,
  transitionStateSuccess,
  updateMacroExperienceSettings,
  updateMacroExperienceSettingsSuccess,
  updateMacroExperienceSettingsFailure,

  updateExperienceBackground,
  updateExperienceBackgroundSuccess,
  updateExperienceBackgroundFailure,

  updateExperienceHeader,
  updateExperienceHeaderSuccess,
  updateExperienceHeaderFailure,

  updatePolicyConfig,
  updatePolicyConfigSuccess,
  updatePolicyConfigFailure,

  hideConfirmDeleteExperienceModal,
  showConfirmDeleteExperienceModal,

  hideConfirmExpireExperienceModal,
  showConfirmExpireExperienceModal,

  cloneExperience,
  cloneExperienceSuccess,
  cloneExperienceFailure,

  fetchExperienceMetrics,
  fetchExperienceMetricsSuccess,
  fetchExperienceMetricsFailure,

  changeMetadata,
  showConfirmUpdateMetadataModal,
  hideConfirmUpdateMetadataModal,
  updateExternalRef,
  leaveExternalRef,
  validateExternalRef,
  validateExternalRefSuccess,
  validateExternalRefFailure,

  resetDirtyFields,

  setQuotaStatus,

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Sharing config.
  toggleShare,
  enableShare,
  disableShare,
  updateSharingDataAttribute,
  updateSharingData,
  checkShareEnabled,
  updatePresentationConfig,
};
